/**
 * DoorAnnotationSection 門標註區段元件
 *
 * 提供門位置標註功能：
 * - 拖曳繪製門
 * - 門類型選擇
 * - 門列表管理
 */

import { Section } from '@/components/layout/Section';
import { SelectInput } from '@/components/shared/FormInput';
import { DoorCanvas } from '@/canvas/door-canvas';
import { plannerStore, setDoors } from '@/store/planner-store';
import type { Door, DoorType } from '@/types';

// ========================================================================
// 型別定義
// ========================================================================

export interface DoorAnnotationSectionOptions {
  /** 確認完成回呼 */
  onComplete?: (doors: Door[]) => void;
  /** 跳過回呼 */
  onSkip?: () => void;
}

// ========================================================================
// DoorAnnotationSection 類別
// ========================================================================

export class DoorAnnotationSection {
  private section: Section;
  private canvas: DoorCanvas | null = null;
  private canvasElement: HTMLCanvasElement | null = null;
  private doorTypeSelect: SelectInput;
  private doorListElement: HTMLDivElement;
  private options: DoorAnnotationSectionOptions;
  private unsubscribe: (() => void) | null = null;

  constructor(options: DoorAnnotationSectionOptions = {}) {
    this.options = options;

    // 建立區段
    this.section = new Section({
      title: '步驟 1.5：標註門的位置',
      description: '在平面圖上標註門的位置，幫助系統規劃更好的動線',
      stepNumber: 1,
      visible: false,
    });

    // 建立門類型選擇器
    this.doorTypeSelect = new SelectInput({
      options: [
        { value: 'main', label: '主要入口' },
        { value: 'normal', label: '普通門' },
        { value: 'emergency', label: '緊急出口' },
      ],
      value: 'main',
    });

    // 建立門列表容器
    this.doorListElement = document.createElement('div');
    this.doorListElement.id = 'doorList';
    this.doorListElement.className = 'space-y-2';

    this.assembleUI();
    this.subscribeToStore();
  }

  // ========================================================================
  // 公開方法
  // ========================================================================

  getElement(): HTMLElement {
    return this.section.getElement();
  }

  /**
   * 設定圖片並初始化 Canvas
   */
  setImage(imageUrl: string): void {
    this.section.show();

    const img = new Image();
    img.onload = () => {
      if (this.canvas) {
        this.canvas.setImage(img);
        this.canvas.render();
      }
    };
    img.src = imageUrl;
  }

  mount(container: HTMLElement): void {
    this.section.mount(container);
  }

  destroy(): void {
    this.unsubscribe?.();
    this.canvas?.destroy();
    this.section.destroy();
  }

  // ========================================================================
  // 私有方法 - UI 建構
  // ========================================================================

  private assembleUI(): void {
    const content = this.section.getContent();

    content.innerHTML = `
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <!-- 左側：Canvas -->
        <div class="lg:col-span-2">
          <div class="relative border border-gray-200 rounded-lg overflow-hidden bg-gray-100">
            <canvas id="doorAnnotationCanvas" class="w-full"></canvas>
          </div>
          <div class="mt-3">
            <p class="text-sm text-gray-600">
              <strong>操作說明：</strong>
            </p>
            <ul class="text-sm text-gray-500 mt-1 space-y-1">
              <li>• 點擊設定起點，再點擊設定終點（Esc 取消）</li>
              <li>• 雙擊門可刪除</li>
              <li>• 門會自動對齊到水平或垂直方向</li>
            </ul>
          </div>
        </div>

        <!-- 右側：控制面板 -->
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">門類型</label>
            <div id="doorTypeContainer"></div>
          </div>

          <div>
            <div class="flex items-center justify-between mb-2">
              <label class="text-sm font-medium text-gray-700">已標註的門</label>
              <span id="doorCount" class="text-sm text-gray-500">0 個</span>
            </div>
            <div id="doorListContainer" class="max-h-48 overflow-y-auto"></div>
          </div>

          <div class="pt-4 border-t border-gray-200 space-y-2">
            <button type="button" id="clearDoorsBtn" class="w-full px-4 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors">
              清除所有門
            </button>
          </div>

          <div class="space-y-2">
            <button type="button" id="skipBtn" class="w-full px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
              跳過此步驟
            </button>
            <button type="button" id="confirmBtn" class="w-full px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors">
              確認並繼續
            </button>
          </div>
        </div>
      </div>
    `;

    // 掛載門類型選擇器
    const doorTypeContainer = content.querySelector('#doorTypeContainer');
    if (doorTypeContainer) {
      this.doorTypeSelect.mount(doorTypeContainer as HTMLElement);
    }

    // 掛載門列表
    const doorListContainer = content.querySelector('#doorListContainer');
    if (doorListContainer) {
      doorListContainer.appendChild(this.doorListElement);
    }

    // 初始化 Canvas
    this.canvasElement = content.querySelector('#doorAnnotationCanvas');
    if (this.canvasElement) {
      this.canvas = new DoorCanvas({
        canvas: this.canvasElement,
        getDoorType: () => this.doorTypeSelect.getValue() as DoorType,
      });
      this.canvas.initialize();
    }

    // 綁定事件
    this.bindEvents(content);
  }

  private bindEvents(content: HTMLElement): void {
    const clearBtn = content.querySelector('#clearDoorsBtn');
    const skipBtn = content.querySelector('#skipBtn');
    const confirmBtn = content.querySelector('#confirmBtn');

    clearBtn?.addEventListener('click', () => this.clearAllDoors());
    skipBtn?.addEventListener('click', () => this.handleSkip());
    confirmBtn?.addEventListener('click', () => this.handleConfirm());
  }

  // ========================================================================
  // 私有方法 - 事件處理
  // ========================================================================

  private clearAllDoors(): void {
    this.canvas?.clearAll();
  }

  private handleSkip(): void {
    setDoors([]);
    this.options.onSkip?.();
  }

  private handleConfirm(): void {
    const doors = this.canvas?.getDoors() ?? [];
    setDoors(doors);
    this.options.onComplete?.(doors);
  }

  private updateDoorList(doors: Door[]): void {
    const countEl = this.section.getElement().querySelector('#doorCount');
    if (countEl) {
      countEl.textContent = `${doors.length} 個`;
    }

    // 🔒 安全性：使用 DOM API 而非 innerHTML 模板字串
    // 清空現有內容
    this.doorListElement.innerHTML = '';

    if (doors.length === 0) {
      const emptyMessage = document.createElement('p');
      emptyMessage.className = 'text-sm text-gray-400 text-center py-4';
      emptyMessage.textContent = '尚未標註任何門';
      this.doorListElement.appendChild(emptyMessage);
      return;
    }

    const typeNames: Record<DoorType, string> = {
      main: '主要入口',
      normal: '普通門',
      emergency: '緊急出口',
    };

    const typeColors: Record<DoorType, string> = {
      main: 'bg-green-100 text-green-800',
      normal: 'bg-blue-100 text-blue-800',
      emergency: 'bg-red-100 text-red-800',
    };

    // 使用 DOM API 建構每個門項目
    doors.forEach((door, index) => {
      const container = document.createElement('div');
      container.className = 'flex items-center justify-between p-2 bg-gray-50 rounded-lg';

      const leftSection = document.createElement('div');
      leftSection.className = 'flex items-center gap-2';

      const nameSpan = document.createElement('span');
      nameSpan.className = 'font-medium text-sm';
      nameSpan.textContent = `門 ${index + 1}`;

      const typeSpan = document.createElement('span');
      typeSpan.className = `px-2 py-0.5 text-xs font-medium rounded-full ${typeColors[door.type]}`;
      typeSpan.textContent = typeNames[door.type];

      leftSection.appendChild(nameSpan);
      leftSection.appendChild(typeSpan);

      const widthSpan = document.createElement('span');
      widthSpan.className = 'text-xs text-gray-500';
      widthSpan.textContent = `${Math.round(door.width)}px`;

      container.appendChild(leftSection);
      container.appendChild(widthSpan);
      this.doorListElement.appendChild(container);
    });
  }

  private subscribeToStore(): void {
    this.unsubscribe = plannerStore.subscribeSelector(
      (state) => state.doors,
      (doors) => {
        this.updateDoorList(doors);
      }
    );
  }
}
